import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './App.css';
import './Sidebar.css';
import TalkToTakku from './TalkToTakku';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Login from './Login';
import DocumentUpload from './DocumentUpload';
import MyDocuments from './MyDocuments';
import AdminDocuments from './AdminDocuments';
import Sidebar from './Sidebar';
// Phase 2 imports
import MemoryManager from './MemoryManager';
import ReminderPanel from './ReminderPanel';
import SettingsModal from './SettingsModal';

import takkuLogo from './assets/takku-logo.png';
import loginIcon from './assets/login-icon.png';

const ADMIN_EMAILS = ['mraveendran13@gmail.com'];

// Types
interface Message {
    role: 'user' | 'assistant';
    content: string;
    timestamp: number;
    image?: string;
    generatedImage?: string;  // For AI-generated images
    sources?: Array<{
        document: string;
        section: number;
        confidence: number;
    }>;
    callStatus?: { active: boolean; call_sid?: string; contact?: string; error?: string };
}

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://takku-gpt-production.up.railway.app';

const App: React.FC = () => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [userId, setUserId] = useState('');
    const [voiceEnabled, setVoiceEnabled] = useState(false);
    const [talkToTakkuOpen, setTalkToTakkuOpen] = useState(false); // full Gemini Live voice mode -- see add_browser_voice_mode.py
    const [isSpeaking, setIsSpeaking] = useState(false);
    // value intentionally unused -- audio-unlock is no longer gated on it,
    // only the setter is kept for the one-time "unlocked" side effect (see unlockAudio)
    const [, setAudioUnlocked] = useState(false);
    // Multi-attachment support
    interface Attachment {
        type: 'image' | 'document';
        name: string;
        content: string; // base64 data URL
    }
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [userToken, setUserToken] = useState<string | null>(null);
    const [currentUser, setCurrentUser] = useState<any>(null);
    const [showLogin, setShowLogin] = useState(false);
    const [showMyDocs, setShowMyDocs] = useState(false);
    const [showAdminDocs, setShowAdminDocs] = useState(false);
    const [docRefresh, setDocRefresh] = useState(0);
    // Phase 2 state
    const [showMemoryManager, setShowMemoryManager] = useState(false);
    const [showReminders, setShowReminders] = useState(false);
    const [showSettings, setShowSettings] = useState(false);

    // Multi-session state
    const [sessions, setSessions] = useState<Array<{id: string, title: string, updated_at: string, is_pinned?: boolean}>>([]);
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);

    // Mode selection state: 'quick' | 'research' | 'formfill'
    const [selectedMode, setSelectedMode] = useState<'quick' | 'research' | 'formfill'>('quick');
    const [researchProgress, setResearchProgress] = useState<string | null>(null);
    const [formFillProgress, setFormFillProgress] = useState<string | null>(null);
    const [modelSelectorOpen, setModelSelectorOpen] = useState(false);
    const modelSelectorRef = useRef<HTMLDivElement>(null);

// Plus menu popup state (Step 1 redesign)
    const [showPlusMenu, setShowPlusMenu] = useState(false);
    const plusMenuRef = useRef<HTMLDivElement>(null);

    // Reminder toast state (for iOS TTS tap-to-hear)
    const [pendingReminderToast, setPendingReminderToast] = useState<{text: string, reminderId?: number} | null>(null);

    const [theme, setTheme] = useState<'silver' | 'night' | 'ocean' | 'cosmos'>(() => {
        return (localStorage.getItem('takku-theme') as 'silver' | 'night' | 'ocean' | 'cosmos') || 'silver';
    });

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);
    // Persistent (always-mounted) <audio> element for Talk to Takku's voice
    // track -- must exist and be play()/pause()'d synchronously inside the
    // mic button's click handler (see unlockAudio()) for iOS Safari to treat
    // it as unlocked. TalkToTakku.tsx's own <audio> only mounts once the
    // overlay opens, which is too late -- a *different* element getting
    // unlocked doesn't help iOS Safari, it's tracked per-element.
    const talkToTakkuAudioRef = useRef<HTMLAudioElement>(null);
    // iOS Safari confirmed STILL silent on Takku's voice even with the
    // <audio> element properly unlocked (real device test, 2026-08-27) --
    // matches multiple long-standing, unresolved WebKit reports of
    // <audio>.srcObject = a live WebRTC MediaStream playing back nothing
    // despite play() resolving with no error (e.g. bugs.webkit.org #230902,
    // Apple dev forum thread 713573). Standard workaround: route the track
    // through the Web Audio API instead of an <audio> element's srcObject --
    // a different code path that doesn't hit this bug. AudioContext must be
    // created/resumed synchronously in the same click-gesture handler as the
    // <audio> unlock (same autoplay-policy reasoning), then reused once
    // Takku's real track arrives.
    const talkToTakkuAudioContextRef = useRef<AudioContext | null>(null);

    // Initialize user ID and fetch history
    useEffect(() => {
        let storedUserId = localStorage.getItem('takku-user-id');
        if (!storedUserId) {
            storedUserId = 'user-' + Math.random().toString(36).substr(2, 9);
            localStorage.setItem('takku-user-id', storedUserId);
        }
        setUserId(storedUserId);

        // Fetch history
        const fetchHistory = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/history`, {
                    headers: { 'X-User-ID': storedUserId }
                });
                
                if (response.data.messages && response.data.messages.length > 0) {
                    setMessages(response.data.messages);
                } else {
                    // Initial greeting if no history
                    setMessages([]); // New personalized greeting handles empty state
                }
            } catch (error) {
                console.error('Failed to fetch history:', error);
                // Let personalized greeting handle empty state
                setMessages([]);
            }
        };
        
        fetchHistory();
    }, []);

    // Auto-scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Apply theme to document
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('takku-theme', theme);
    }, [theme]);

    // Close model selector when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (modelSelectorRef.current && !modelSelectorRef.current.contains(event.target as Node)) {
                setModelSelectorOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    // Close plus menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (plusMenuRef.current && !plusMenuRef.current.contains(event.target as Node)) {
                setShowPlusMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);


    // Auth persistence
    useEffect(() => {
        const savedToken = localStorage.getItem('takku-token');
        const savedUser = localStorage.getItem('takku-user');
        if (savedToken && savedUser) {
            setUserToken(savedToken);
            setCurrentUser(JSON.parse(savedUser));

            // Re-subscribe to push notifications on load (in case subscription expired)
            // eslint-disable-next-line react-hooks/exhaustive-deps
            subscribeToPushNotifications(savedToken).catch(err => {
                console.log('Push re-subscription skipped:', err);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync voice preference to backend when it changes
    useEffect(() => {
        localStorage.setItem('takku-voice-enabled', String(voiceEnabled));

        // Sync to backend if user is logged in
        if (userToken) {
            axios.put(`${API_BASE_URL}/preferences/voice`, null, {
                params: { enabled: voiceEnabled },
                headers: { Authorization: `Bearer ${userToken}` }
            }).catch(err => {
                console.log('Voice preference sync failed:', err);
            });
        }
    }, [voiceEnabled, userToken]);

    // Send API URL to Service Worker for dismiss endpoint calls
    useEffect(() => {
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.ready.then(registration => {
                if (registration.active) {
                    registration.active.postMessage({
                        type: 'SET_API_URL',
                        url: API_BASE_URL
                    });
                    console.log('Sent API URL to Service Worker:', API_BASE_URL);
                }
            }).catch(err => {
                console.log('Failed to send API URL to SW:', err);
            });
        }
    }, []);

    // Detect iOS/mobile for TTS tap-to-hear requirement
    const isIOSorMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    // Listen for service worker messages (for reminder TTS and dismiss notifications)
    useEffect(() => {
        const handleServiceWorkerMessage = (event: MessageEvent) => {
            if (event.data?.type === 'SPEAK_REMINDER' && voiceEnabled) {
                const reminderText = event.data.body;

                if (isIOSorMobile) {
                    // On iOS/mobile, show toast for tap-to-hear (audio requires user gesture)
                    setPendingReminderToast({
                        text: reminderText,
                        reminderId: event.data.reminderId
                    });
                } else {
                    // On desktop, try to auto-speak
                    speakReminderText(`Reminder: ${reminderText}`);
                }
            }
            if (event.data?.type === 'REMINDER_DISMISSED') {
                console.log('Reminder dismissed:', event.data.reminderId);
                // Could refresh reminder list here if needed
            }
        };

        // Speak reminder function (defined inline to avoid dependency issues)
        const speakReminderText = (text: string) => {
            // Cancel any current speech
            window.speechSynthesis.cancel();

            const speak = () => {
                const utterance = new SpeechSynthesisUtterance(text);
                utterance.lang = 'en-US';
                utterance.rate = 0.9; // Slightly slower for reminders
                utterance.pitch = 1.1; // Slightly higher pitch for attention
                utterance.volume = 1.0;

                const voices = window.speechSynthesis.getVoices();
                const englishVoice = voices.find(v => v.lang.startsWith('en'));
                if (englishVoice) {
                    utterance.voice = englishVoice;
                }

                window.speechSynthesis.speak(utterance);
            };

            if (window.speechSynthesis.getVoices().length === 0) {
                window.speechSynthesis.onvoiceschanged = () => {
                    speak();
                    window.speechSynthesis.onvoiceschanged = null;
                };
                setTimeout(speak, 100);
            } else {
                speak();
            }
        };

        navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);
        return () => {
            navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
        };
    }, [voiceEnabled, isIOSorMobile]);

    // Handle tap-to-hear for iOS/mobile reminder toast
    const handleTapToHear = () => {
        if (!pendingReminderToast) return;

        // Cancel any current speech
        window.speechSynthesis.cancel();

        const text = `Reminder: ${pendingReminderToast.text}`;
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'en-US';
        utterance.rate = 0.9;
        utterance.pitch = 1.1;
        utterance.volume = 1.0;

        const voices = window.speechSynthesis.getVoices();
        const englishVoice = voices.find(v => v.lang.startsWith('en'));
        if (englishVoice) {
            utterance.voice = englishVoice;
        }

        window.speechSynthesis.speak(utterance);

        // Dismiss the toast
        setPendingReminderToast(null);
    };

    // Dismiss reminder toast without speaking
    const dismissReminderToast = () => {
        setPendingReminderToast(null);
    };

    // Unlock audio for iOS Safari (must be called from user gesture)
    const unlockAudio = () => {
        // Deliberately NOT gated behind "if (audioUnlocked) return" anymore --
        // confirmed live (2026-08-27) that gate broke a *second* Talk to
        // Takku attempt in the same page session, even on desktop Chrome
        // which had worked cleanly the first time. Browsers auto-suspend an
        // idle AudioContext as a power-saving measure; skipping .resume() on
        // every call after the first meant a context that got suspended
        // between attempts never woke back up. Every operation here is cheap
        // and idempotent, so there's no real reason to skip repeats.

        // iOS Safari requires a user gesture to enable audio
        const utterance = new SpeechSynthesisUtterance('');
        utterance.volume = 0;
        window.speechSynthesis.speak(utterance);
        window.speechSynthesis.cancel();

        // The above only unlocks window.speechSynthesis (used by speakText()
        // for chat TTS) -- it does NOT unlock a plain <audio> element playing
        // a MediaStream, which is what Talk to Takku's <audio> tag does with
        // Takku's WebRTC voice track. Confirmed live on an iPhone (iOS 18.7
        // Safari): connection worked, mic worked (server transcribed her
        // speech fine), but zero audio ever came out -- TalkToTakku.tsx's
        // audioRef.play() was silently rejected by Safari's autoplay policy
        // and swallowed by its .catch(() => {}). A real HTMLMediaElement
        // play()+pause() here, still synchronously inside this same click
        // handler, is the actual unlock Safari checks for; per-element, not
        // per-page, so this specific element needs to be the one that later
        // gets Takku's track (see the persistent ref TalkToTakku.tsx now
        // takes as a prop instead of creating its own <audio> element).
        if (talkToTakkuAudioRef.current) {
            talkToTakkuAudioRef.current.play().then(() => {
                talkToTakkuAudioRef.current?.pause();
            }).catch(() => {});
        }

        // Confirmed live (2026-08-27): the <audio> element unlock above
        // wasn't enough -- still silent on iOS Safari. This is the real
        // fix; see talkToTakkuAudioContextRef's comment.
        if (!talkToTakkuAudioContextRef.current) {
            const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
            if (AudioContextClass) {
                talkToTakkuAudioContextRef.current = new AudioContextClass();
            }
        }
        talkToTakkuAudioContextRef.current?.resume().catch(() => {});

        setAudioUnlocked(true);
        console.log('Audio unlocked for iOS');
    };

    // Browser TTS Function
    const speakText = (text: string) => {
        if (!voiceEnabled || isSpeaking) return;

        // Strip emojis and markdown symbols for speech
        const cleanText = text
            .replace(/([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g, '')
            .replace(/[*_#`]/g, '');

        if (!cleanText.trim()) return;

        // Cancel any current speech
        window.speechSynthesis.cancel();

        // iOS Safari workaround: voices may not be loaded yet
        const speak = () => {
            const utterance = new SpeechSynthesisUtterance(cleanText);
            utterance.lang = 'en-US';
            utterance.rate = 1.0;
            utterance.pitch = 1.0;
            utterance.volume = 1.0;

            // Try to get a voice (iOS sometimes needs this)
            const voices = window.speechSynthesis.getVoices();
            const englishVoice = voices.find(v => v.lang.startsWith('en'));
            if (englishVoice) {
                utterance.voice = englishVoice;
            }

            utterance.onstart = () => setIsSpeaking(true);
            utterance.onend = () => setIsSpeaking(false);
            utterance.onerror = (e) => {
                console.error('Speech error:', e);
                setIsSpeaking(false);
            };

            window.speechSynthesis.speak(utterance);
        };

        // iOS Safari: voices load asynchronously
        if (window.speechSynthesis.getVoices().length === 0) {
            window.speechSynthesis.onvoiceschanged = () => {
                speak();
                window.speechSynthesis.onvoiceschanged = null;
            };
            // Fallback if voices don't load
            setTimeout(speak, 100);
        } else {
            speak();
        }
    };

    const stopSpeaking = () => {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
    };

    // Handle paste for images (multi-attachment)
    const handlePaste = async (e: React.ClipboardEvent) => {
        const items = e.clipboardData.items;
        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
                e.preventDefault();
                const blob = items[i].getAsFile();
                if (blob) {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                        if (event.target?.result) {
                            setAttachments(prev => [...prev, {
                                type: 'image',
                                name: 'Pasted image',
                                content: event.target!.result as string
                            }]);
                        }
                    };
                    reader.readAsDataURL(blob);
                }
                return;
            }
        }
    };

    // Handle file upload (multi-attachment)
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        Array.from(files).forEach(file => {
            const reader = new FileReader();
            reader.onload = (event) => {
                if (event.target?.result) {
                    const result = event.target.result as string;
                    const attType = file.type.startsWith('image/') ? 'image' : 'document';
                    setAttachments(prev => [...prev, {
                        type: attType as 'image' | 'document',
                        name: file.type.startsWith('image/') ? file.name || 'Image' : file.name,
                        content: result
                    }]);
                }
            };
            reader.readAsDataURL(file);
        });
        // Reset input so same file can be re-selected
        e.target.value = '';
    };

    // Auth handlers
    const handleLoginSuccess = async (token: string, user: any) => {
        setUserToken(token);
        setCurrentUser(user);
        setShowLogin(false);
        localStorage.setItem('takku-token', token);
        localStorage.setItem('takku-user', JSON.stringify(user));

        // Subscribe to push notifications after login
        try {
            await subscribeToPushNotifications(token);
        } catch (err) {
            console.log('Push subscription skipped:', err);
        }
    };

    // Push notification subscription helper
    const subscribeToPushNotifications = async (token: string) => {
        // Check if push is supported
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            console.log('Push notifications not supported');
            return;
        }

        try {
            // Request notification permission
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                console.log('Notification permission denied');
                return;
            }

            // Get VAPID public key from backend
            let vapidPublicKey = '';
            try {
                const vapidResponse = await axios.get(`${API_BASE_URL}/push/vapid-public-key`);
                vapidPublicKey = vapidResponse.data.publicKey || '';
            } catch {
                // Push not configured on server - silently skip
                return;
            }

            if (!vapidPublicKey || vapidPublicKey.length < 10) {
                // VAPID key not configured - silently skip
                return;
            }

            // Wait for service worker to be ready
            const registration = await navigator.serviceWorker.ready;

            // Subscribe to push
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
            });

            // Send subscription to backend
            const subscriptionJson = subscription.toJSON();
            await axios.post(
                `${API_BASE_URL}/push/subscribe`,
                {
                    endpoint: subscriptionJson.endpoint,
                    p256dh: subscriptionJson.keys?.p256dh || '',
                    auth: subscriptionJson.keys?.auth || ''
                },
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            console.log('Push notification subscription successful');
        } catch {
            // Push subscription failed - silently ignore (not critical)
        }
    };

    // Helper function to convert VAPID key
    const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
        const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding)
            .replace(/-/g, '+')
            .replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    };


    // === SESSION MANAGEMENT ===
    const createNewSession = async () => {
        // Clear current chat first for immediate feedback
        setMessages([]); // New personalized greeting handles empty state
        setSelectedMode('quick'); // Reset to default mode for new chats

        if (!userToken) {
            setCurrentSessionId(null);
            return;
        }
        try {
            const res = await axios.post(`${API_BASE_URL}/sessions`, null, {
                headers: { Authorization: `Bearer ${userToken}` },
                params: { title: 'New Chat' }
            });
            if (res.data?.id) {
                setSessions(prev => [res.data, ...prev]);
                setCurrentSessionId(res.data.id);
            }
        } catch (err) {
            console.error('Failed to create session:', err);
            // Still allow chat even if session creation fails
            setCurrentSessionId(null);
        }
    };

    const fetchSessionHistory = async (sessionId: string) => {
        if (!userToken) return;
        try {
            const res = await axios.get(`${API_BASE_URL}/sessions/${sessionId}/history`, {
                headers: { Authorization: `Bearer ${userToken}` }
            });
            if (Array.isArray(res.data) && res.data.length > 0) {
                const history = res.data.map((m: any) => ({
                    role: m.role as 'user' | 'assistant',
                    content: m.content,
                    timestamp: new Date(m.created_at).getTime()
                }));
                setMessages(history);
            } else {
                setMessages([]); // New personalized greeting handles empty state
            }
        } catch (err) { console.error('Failed to fetch session history:', err); }
    };

    const selectSession = (sessionId: string) => {
        setCurrentSessionId(sessionId);
        fetchSessionHistory(sessionId);
    };

    const deleteSession = async (sessionId: string) => {
        if (!userToken) return;
        try {
            await axios.delete(`${API_BASE_URL}/sessions/${sessionId}`, {
                headers: { Authorization: `Bearer ${userToken}` }
            });
            setSessions(prev => prev.filter(s => s.id !== sessionId));
            if (currentSessionId === sessionId) {
                setCurrentSessionId(null);
                setMessages([]); // New personalized greeting handles empty state
            }
        } catch (err) { console.error('Failed to delete session:', err); }
    };

    const renameSession = async (sessionId: string, newTitle: string) => {
        if (!userToken) return;
        try {
            await axios.patch(`${API_BASE_URL}/sessions/${sessionId}/rename`, null, {
                headers: { Authorization: `Bearer ${userToken}` },
                params: { title: newTitle }
            });
            setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, title: newTitle } : s));
        } catch (err) { console.error('Failed to rename session:', err); }
    };

    const pinSession = async (sessionId: string) => {
        if (!userToken) return;
        try {
            const res = await axios.patch(`${API_BASE_URL}/sessions/${sessionId}/pin`, null, {
                headers: { Authorization: `Bearer ${userToken}` }
            });
            setSessions(prev => {
                const updated = prev.map(s => s.id === sessionId ? { ...s, is_pinned: res.data.is_pinned } : s);
                // Sort: pinned first, then by most recent
                return updated.sort((a, b) => {
                    if (a.is_pinned && !b.is_pinned) return -1;
                    if (!a.is_pinned && b.is_pinned) return 1;
                    return 0;
                });
            });
        } catch (err) { console.error('Failed to pin session:', err); }
    };

    const shareSession = async (sessionId: string) => {
        if (!userToken) return;
        try {
            const res = await axios.post(`${API_BASE_URL}/sessions/${sessionId}/share`, null, {
                headers: { Authorization: `Bearer ${userToken}` }
            });
            const shareUrl = `${window.location.origin}/shared/${res.data.share_id}`;
            await navigator.clipboard.writeText(shareUrl);
            alert(`Share link copied to clipboard!\n\n${shareUrl}`);
        } catch (err) { console.error('Failed to share session:', err); }
    };

    // Fetch sessions when logged in
    useEffect(() => {
        const loadSessions = async () => {
            if (!userToken) return;
            try {
                const res = await axios.get(`${API_BASE_URL}/sessions`, {
                    headers: { Authorization: `Bearer ${userToken}` }
                });
                if (Array.isArray(res.data)) {
                    setSessions(res.data);
                    if (res.data.length > 0) {
                        setCurrentSessionId(res.data[0].id);
                    }
                }
            } catch (err) { console.error('Failed to fetch sessions:', err); }
        };
        loadSessions();
    }, [userToken]);
    // === END SESSION MANAGEMENT ===

    const handleLogout = () => {
        setUserToken(null);
        setCurrentUser(null);
        localStorage.removeItem('takku-token');
        localStorage.removeItem('takku-user');
    };

    // Generate image using FAL.ai
    const generateImage = async (prompt: string): Promise<string | null> => {
        try {
            const response = await axios.post(
                `${API_BASE_URL}/generate-image`,
                { prompt, size: "square_hd" },
                { timeout: 60000 }  // 60 second timeout for image generation
            );
            return response.data.image_url;
        } catch (error: any) {
            console.error('Image generation error:', error);
            return null;
        }
    };

    
    // Deep Research Handler
    const handleDeepResearch = async (query: string) => {
        setResearchProgress('starting');

        try {
            const headers: any = {
                'Content-Type': 'application/json'
            };
            if (userToken) {
                headers['Authorization'] = `Bearer ${userToken}`;
            } else {
                headers['X-User-ID'] = userId;
            }

            setResearchProgress('researching');

            const response = await axios.post(
                `${API_BASE_URL}/research`,
                { query, report_type: 'research_report' },
                { headers, timeout: 300000 }  // 5 minute timeout
            );

            const researchMessage: Message = {
                role: 'assistant',
                content: response.data.report,
                timestamp: Date.now(),
                sources: response.data.sources?.map((url: string, i: number) => ({
                    document: url,
                    section: i + 1,
                    confidence: 1.0
                }))
            };

            setMessages(prev => [...prev, researchMessage]);

        } catch (error: any) {
            console.error('Deep Research Error:', error);
            const errorMessage: Message = {
                role: 'assistant',
                content: `Deep research failed: ${error.response?.data?.detail || error.message || 'Unknown error'}. Please try again or use Quick mode.`,
                timestamp: Date.now()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setResearchProgress(null);
        }
    };

    // Form Fill Handler
    const handleFormFill = async (formDataText: string) => {
        const firstDoc = attachments.find(a => a.type === 'document');
        if (!firstDoc) {
            const errorMessage: Message = {
                role: 'assistant',
                content: 'Please attach a PDF or Word document first, then describe the data to fill.',
                timestamp: Date.now()
            };
            setMessages(prev => [...prev, errorMessage]);
            return;
        }

        setFormFillProgress('processing');

        try {
            // Parse form data from user's message
            // User can provide data in natural language or JSON format
            let formData: Record<string, string> = {};

            // Try to extract key-value pairs from the message
            // Format: "name: John, email: john@example.com" or JSON
            if (formDataText.includes(':')) {
                const pairs = formDataText.split(/[,\n]+/);
                pairs.forEach(pair => {
                    const [key, ...valueParts] = pair.split(':');
                    if (key && valueParts.length > 0) {
                        formData[key.trim()] = valueParts.join(':').trim();
                    }
                });
            }

            // If no structured data found, send the whole message as instructions
            if (Object.keys(formData).length === 0) {
                formData = { _raw_input: formDataText };
            }

            setFormFillProgress('filling');

            // Create FormData for file upload
            const formPayload = new FormData();

            // Decode base64 document back to file
            const base64Data = firstDoc.content.split(',')[1] || firstDoc.content;
            const binaryString = atob(base64Data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            const blob = new Blob([bytes]);
            formPayload.append('file', blob, firstDoc.name);
            formPayload.append('form_data', JSON.stringify(formData));
            formPayload.append('instructions', formDataText);

            const response = await axios.post(
                `${API_BASE_URL}/fill-form`,
                formPayload,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        ...(userToken ? { 'Authorization': `Bearer ${userToken}` } : {})
                    },
                    timeout: 60000
                }
            );

            if (response.data.success) {
                // Create download link for filled document
                const filledFileBase64 = response.data.filled_file;
                const filename = response.data.filename;
                const fieldsCount = response.data.fields_filled?.length || 0;

                // Determine mime type
                const mimeType = filename.endsWith('.pdf')
                    ? 'application/pdf'
                    : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

                const downloadUrl = `data:${mimeType};base64,${filledFileBase64}`;

                // Trigger automatic download
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                const resultMessage: Message = {
                    role: 'assistant',
                    content: `**Form filled successfully!**\n\nFilled ${fieldsCount} fields in your document.\n\nYour file **${filename}** has been downloaded.\n\n*Fields filled: ${response.data.fields_filled?.join(', ') || 'N/A'}*`,
                    timestamp: Date.now()
                };

                setMessages(prev => [...prev, resultMessage]);
            } else {
                throw new Error(response.data.message || 'Form filling failed');
            }

        } catch (error: any) {
            console.error('Form Fill Error:', error);
            const errorMessage: Message = {
                role: 'assistant',
                content: `Form filling failed: ${error.response?.data?.detail || error.message || 'Unknown error'}\n\n**Tips:**\n- Make sure the PDF has fillable form fields\n- For Word docs, use placeholders like {name}, [email], etc.\n- Try providing data in format: "name: John, email: john@example.com"`,
                timestamp: Date.now()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setFormFillProgress(null);
            setAttachments([]);  // Clear attachments after processing
        }
    };

    // Send message
    const sendMessage = async () => {
        if (!input.trim() || loading || !userId) return;

        const messageContent = input.trim();

        // Handle Deep Research mode
        if (selectedMode === 'research') {
            const userMessage: Message = {
                role: 'user',
                content: `[Deep Research] ${messageContent}`,
                timestamp: Date.now()
            };
            setInput('');
            setMessages(prev => [...prev, userMessage]);
            setLoading(true);
            await handleDeepResearch(messageContent);
            setLoading(false);
            return;
        }


        // Handle Form Fill mode
        if (selectedMode === 'formfill') {
            const userMessage: Message = {
                role: 'user',
                content: `[Form Fill] ${messageContent}`,
                timestamp: Date.now()
            };
            setInput('');
            setMessages(prev => [...prev, userMessage]);
            setLoading(true);
            await handleFormFill(messageContent);
            setLoading(false);
            return;
        }
        
        // --- Smart Image Detection ---
        const imageRegex = /^(draw|generate|create|make) (me )?(a |an )?(image|picture|pic|photo|art)s? (of )?/i;
        const simpleDrawRegex = /^draw (me )?(a |an )?/i;
        
        let isImageRequest = false;
        let prompt = "";
        
        // 1. Check for explicit /imagine command
        if (messageContent.toLowerCase().startsWith('/imagine ')) {
            isImageRequest = true;
            prompt = messageContent.slice(9).trim();
        } 
        // 2. Check for natural language triggers
        else if (imageRegex.test(messageContent) || simpleDrawRegex.test(messageContent)) {
            isImageRequest = true;
            // Extract prompt by removing the trigger phrase
            prompt = messageContent.replace(imageRegex, "").replace(simpleDrawRegex, "").trim();
        }
        
        if (isImageRequest) {
            if (!prompt) {
                setInput('');
                return;
            }
            
            const userMessage: Message = {
                role: 'user',
                content: messageContent,
                timestamp: Date.now()
            };
            
            setInput('');
            setMessages(prev => [...prev, userMessage]);
            setLoading(true);
            
            const imageUrl = await generateImage(prompt);
            
            const aiMessage: Message = {
                role: 'assistant',
                content: imageUrl 
                    ? `🎨 Here's your generated image for: "${prompt}"`
                    : `❌ Failed to generate image. Please try again or check your prompt.`,
                timestamp: Date.now(),
                generatedImage: imageUrl || undefined
            };
            
            setMessages(prev => [...prev, aiMessage]);
            setLoading(false);
            return;
        }
        
        const firstImage = attachments.find(a => a.type === 'image');
        const userMessage: Message = {
            role: 'user',
            content: messageContent,
            timestamp: Date.now(),
            image: firstImage?.content
        };

        setInput('');
        setMessages(prev => [...prev, userMessage]);
        setLoading(true);

        try {
            const headers: any = {
                'X-Character': 'takku',
                'Content-Type': 'application/json'
            };

            // Add Authorization if user is logged in
            if (userToken) {
                headers['Authorization'] = `Bearer ${userToken}`;
            } else {
                headers['X-User-ID'] = userId; // Fallback for non-logged users
            }

            const response = await axios.post(
                `${API_BASE_URL}/chat`,
                {
                    message: messageContent,
                    symptoms: '',
                    use_web_search: true,
                    attachments: attachments.map(a => ({ type: a.type, name: a.name, content: a.content })),
                    // Legacy backward compat
                    image: attachments.find(a => a.type === 'image')?.content || null,
                    document_content: attachments.find(a => a.type === 'document')?.content || null,
                    document_name: attachments.find(a => a.type === 'document')?.name || null,
                    session_id: currentSessionId
                },
                {
                    headers,
                    timeout: 60000
                }
            );

            const aiMessage: Message = {
                role: 'assistant',
                content: response.data.response || "Sorry, I couldn't generate a response.",
                timestamp: Date.now(),
                sources: response.data.sources,
                callStatus: response.data.call_status
            };

            setMessages(prev => [...prev, aiMessage]);

            // Clear attachments after sending
            if (attachments.length > 0) {
                setAttachments([]);
            }

            // Speak response if voice enabled
            if (voiceEnabled) {
                speakText(aiMessage.content);
            }
        } catch (error: any) {
            console.error('API Error:', error);

            const errorMessage: Message = {
                role: 'assistant',
                content: "Sorry, I encountered an error. Please try again!",
                timestamp: Date.now()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text);
    };

    const isAdmin = currentUser && ADMIN_EMAILS.includes(currentUser.email);

    // Helper: Get time-based greeting
    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good morning';
        if (hour < 17) return 'Good afternoon';
        return 'Good evening';
    };

    // Helper: Get user's first name
    const getUserFirstName = () => {
        if (!currentUser) return null;
        if (currentUser.name) {
            return currentUser.name.split(' ')[0];
        }
        if (currentUser.email) {
            return currentUser.email.split('@')[0];
        }
        return null;
    };



    return (
        <>
            {/* Reminder Toast for iOS/Mobile tap-to-hear */}
            {pendingReminderToast && (
                <div className="reminder-toast-overlay">
                    <div className="reminder-toast">
                        <div className="reminder-toast-icon">🔔</div>
                        <div className="reminder-toast-content">
                            <div className="reminder-toast-title">Reminder</div>
                            <div className="reminder-toast-text">{pendingReminderToast.text}</div>
                        </div>
                        <div className="reminder-toast-actions">
                            <button className="reminder-toast-speak" onClick={handleTapToHear}>
                                🔊 Tap to Hear
                            </button>
                            <button className="reminder-toast-dismiss" onClick={dismissReminderToast}>
                                ✕
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Always mounted (not inside TalkToTakku's conditional render) so
                unlockAudio() can play()/pause() it synchronously from the mic
                button's click handler, before the overlay -- and this same
                element -- even opens. See talkToTakkuAudioRef's comment. */}
            <audio ref={talkToTakkuAudioRef} />
            <TalkToTakku
                isOpen={talkToTakkuOpen}
                onClose={() => setTalkToTakkuOpen(false)}
                apiBaseUrl={API_BASE_URL}
                userToken={userToken}
                audioRef={talkToTakkuAudioRef}
                audioContextRef={talkToTakkuAudioContextRef}
            />

            <div className="amber-gradient" />
            <div className={`takku-app ${sidebarOpen ? 'sidebar-open' : ''}`}>
                {/* Sidebar for logged-in users */}
                {userToken && (
                    <Sidebar
                        sessions={sessions}
                        currentSessionId={currentSessionId}
                        onSelectSession={selectSession}
                        onNewChat={createNewSession}
                        onDeleteSession={deleteSession}
                        onRenameSession={renameSession}
                        onPinSession={pinSession}
                        onShareSession={shareSession}
                        isOpen={sidebarOpen}
                        toggleSidebar={() => setSidebarOpen(!sidebarOpen)}
                        currentUser={currentUser}
                        onOpenSettings={() => setShowSettings(true)}
                        onLogout={handleLogout}
                        onOpenMemory={() => setShowMemoryManager(true)}
                        onOpenReminders={() => setShowReminders(true)}
                        onOpenMyDocs={() => setShowMyDocs(true)}
                        onAskTakkuToCall={() => {
                            setInput("Call ");
                            setSidebarOpen(false);
                            setTimeout(() => {
                                const textarea = document.querySelector('.chat-input');
                                if (textarea) (textarea as HTMLTextAreaElement).focus();
                            }, 100);
                        }}
                    />
                )}
                {/* Header - Redesigned Step 2 */}
                <header className="takku-header redesigned">
                    {/* Left: Menu toggle (for logged-in users) or empty space */}
                    <div className="header-left">
                        {userToken ? (
                            <button
                                className="menu-toggle-btn"
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                aria-label="Toggle menu"
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="3" y1="6" x2="21" y2="6"/>
                                    <line x1="3" y1="12" x2="21" y2="12"/>
                                    <line x1="3" y1="18" x2="21" y2="18"/>
                                </svg>
                            </button>
                        ) : (
                            <div className="header-spacer"></div>
                        )}
                    </div>

                    {/* Center: Empty - branding moved to sidebar */}
                    <div className="header-center"></div>

                    {/* Right: Account icon for settings/login */}
                    <div className="header-right">
                        {currentUser ? (
                            <button
                                className="mascot-btn"
                                onClick={() => setShowSettings(true)}
                                aria-label="Settings & Profile"
                            >
                                <img src={loginIcon} alt="Settings" className="mascot-icon" />
                            </button>
                        ) : (
                            <button
                                className="login-mascot-btn"
                                onClick={() => setShowLogin(true)}
                                aria-label="Login"
                            >
                                <img src={loginIcon} alt="Login" className="mascot-icon" />
                            </button>
                        )}
                    </div>
                </header>
                {/* Messages */}
                <div className="messages-area">
                    {/* Personalized Greeting - shows when no messages */}
                    {messages.length <= 1 && (
                        <div className="welcome-greeting">
                            <div className="greeting-logo">
                                <img src={takkuLogo} alt="Takku" className="greeting-mascot" />
                            </div>
                            <h2 className="greeting-text">
                                {getUserFirstName()
                                    ? `${getGreeting()}, ${getUserFirstName()}`
                                    : getGreeting()
                                }
                            </h2>
                            <p className="greeting-subtext">How can I help you today?</p>
                        </div>
                    )}

                    {messages.map((message, index) => (
                        <div key={index} className={`message ${message.role}`}>
                            {message.role === 'assistant' && (
                                <div className="message-avatar">
                                    <img
                                        src={takkuLogo}
                                        alt="Takku"
                                        className={isSpeaking && index === messages.length - 1 ? 'speaking' : ''}
                                    />
                                </div>
                            )}
                            <div className="message-content">
                                <div className="message-header">
                                    <span className="message-role">
                                        {message.role === 'user' ? 'You' : 'Takku'}
                                    </span>
                                    <button
                                        className="copy-btn"
                                        onClick={() => handleCopy(message.content)}
                                        title="Copy message"
                                    >
                                        📋
                                    </button>
                                </div>

                                {message.image && (
                                    <div className="message-image">
                                        <img src={message.image} alt="User upload" />
                                    </div>
                                )}
                                <div className="message-text">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {message.content}
                                    </ReactMarkdown>
                                </div>
                                {message.generatedImage && (
                                    <div className="generated-image">
                                        <img src={message.generatedImage} alt="AI Generated" />
                                        <a
                                            href={message.generatedImage}
                                            download={`takku-image-${message.timestamp}.png`}
                                            className="download-btn"
                                        >
                                            ⬇️ Download Full Size
                                        </a>
                                    </div>
                                )}
                                {message.sources && message.sources.length > 0 && (
                                    <div className="sources">
                                        <div className="sources-title">📚 Sources:</div>
                                        {message.sources.map((source, idx) => (
                                            <div key={idx} className="source-item">
                                                {source.document} (Section {source.section})
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className="message-time">
                                    {new Date(message.timestamp).toLocaleTimeString()}
                                </div>
                            </div>
                        </div>
                    ))}

                    {loading && (
                        <div className="message assistant">
                            <div className="message-avatar">
                                <img src={takkuLogo} alt="Takku" className="speaking" />
                            </div>
                            <div className="message-content">
                                <div className="typing-indicator">
                                    <span></span><span></span><span></span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>


            {/* Voice Controls */}
                {isSpeaking && (
                    <div className="voice-controls">
                        <span className="speaking-indicator">🎙️ Speaking...</span>
                        <button className="stop-btn" onClick={stopSpeaking}>
                            ⏹️ Stop
                        </button>
                    </div>
                )}

                {/* Input Area */}
                <div className="input-area">
                    {/* Research Progress Indicator */}
                        {researchProgress && (
                            <div className="research-progress">
                                <div className="research-progress-header">
                                    <div className="research-spinner"></div>
                                    <span>Deep Research in Progress</span>
                                </div>
                                <div className="research-progress-steps">
                                    <div className={`progress-step ${researchProgress === 'starting' ? 'active' : 'completed'}`}>
                                        <span className="step-icon">{researchProgress === 'starting' ? '...' : '...'}</span>
                                        <span>Initializing research agents</span>
                                    </div>
                                    <div className={`progress-step ${researchProgress === 'researching' ? 'active' : ''}`}>
                                        <span className="step-icon">{researchProgress === 'researching' ? '...' : ''}</span>
                                        <span>Searching and analyzing sources</span>
                                    </div>
                                </div>
                                <p className="research-time-estimate">This typically takes 2-5 minutes...</p>
                            </div>
                        )}


                        {/* Form Fill Progress Indicator */}
                        {formFillProgress && (
                            <div className="formfill-progress">
                                <div className="formfill-progress-header">
                                    <div className="formfill-spinner"></div>
                                    <span>Filling Your Form</span>
                                </div>
                                <div className="formfill-progress-steps">
                                    <div className={`progress-step ${formFillProgress === 'processing' ? 'active' : 'completed'}`}>
                                        <span className="step-icon">{formFillProgress === 'processing' ? '...' : '...'}</span>
                                        <span>Analyzing form fields</span>
                                    </div>
                                    <div className={`progress-step ${formFillProgress === 'filling' ? 'active' : ''}`}>
                                        <span className="step-icon">{formFillProgress === 'filling' ? '...' : ''}</span>
                                        <span>Filling with your data</span>
                                    </div>
                                </div>
                                <p className="formfill-time-estimate">This typically takes 10-30 seconds...</p>
                            </div>
                        )}

                        {/* Attachment Chips - Above Input (Multi-file) */}
                        {attachments.length > 0 && (
                            <div className="attachment-chips">
                                {attachments.map((att, idx) => (
                                    <div key={idx} className={`attachment-chip ${att.type}-chip`}>
                                        {att.type === 'image' ? (
                                            <img src={att.content} alt="Preview" className="attachment-thumbnail" />
                                        ) : (
                                            <svg className="attachment-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                                <polyline points="14 2 14 8 20 8"/>
                                                <line x1="16" y1="13" x2="8" y2="13"/>
                                                <line x1="16" y1="17" x2="8" y2="17"/>
                                                <polyline points="10 9 9 9 8 9"/>
                                            </svg>
                                        )}
                                        <span className="attachment-name">{att.name}</span>
                                        <button
                                            className="attachment-remove"
                                            onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                                            aria-label={`Remove ${att.type}`}
                                        >
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <line x1="18" y1="6" x2="6" y2="18"/>
                                                <line x1="6" y1="6" x2="18" y2="18"/>
                                            </svg>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="input-wrapper">
                        {/* Plus Button for Features Menu */}
                        <div className="plus-menu-container" ref={plusMenuRef}>
                            <button
                                className="plus-btn"
                                onClick={() => setShowPlusMenu(!showPlusMenu)}
                                disabled={loading}
                                aria-label="More options"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="12" y1="5" x2="12" y2="19"/>
                                    <line x1="5" y1="12" x2="19" y2="12"/>
                                </svg>
                            </button>

                            {/* Plus Menu Popup - Phase 1 Redesign */}
                            {showPlusMenu && (
                                <div className="plus-menu-popup redesigned">
                                    {/* Top Row: 3 Action Boxes */}
                                    <div className="plus-menu-top-row">
                                        <button
                                            className="plus-menu-box"
                                            onClick={() => {
                                                cameraInputRef.current?.click();
                                                setShowPlusMenu(false);
                                            }}
                                        >
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                                                <circle cx="12" cy="13" r="4"/>
                                            </svg>
                                            <span>Camera</span>
                                        </button>

                                        {userToken && (
                                            <button
                                                className="plus-menu-box"
                                                onClick={() => {
                                                    document.getElementById('doc-upload')?.click();
                                                    setShowPlusMenu(false);
                                                }}
                                            >
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                    <path d="M4 4h16v16H4z"/>
                                                    <path d="M4 12h16"/>
                                                    <path d="M12 4v16"/>
                                                    <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
                                                    <circle cx="16" cy="8" r="1.5" fill="currentColor"/>
                                                    <circle cx="8" cy="16" r="1.5" fill="currentColor"/>
                                                    <circle cx="16" cy="16" r="1.5" fill="currentColor"/>
                                                </svg>
                                                <span>RAG</span>
                                            </button>
                                        )}

                                        <button
                                            className="plus-menu-box"
                                            onClick={() => {
                                                fileInputRef.current?.click();
                                                setShowPlusMenu(false);
                                            }}
                                        >
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                                            </svg>
                                            <span>Attach</span>
                                        </button>
                                    </div>

                                    {/* List Items */}
                                    <div className="plus-menu-list">
                                        <button
                                            className={`plus-menu-item compact ${voiceEnabled ? 'active' : ''}`}
                                            onClick={() => {
                                                unlockAudio();
                                                setVoiceEnabled(!voiceEnabled);
                                                setShowPlusMenu(false);
                                            }}
                                        >
                                            <span className="plus-menu-icon">
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    {voiceEnabled ? (
                                                        <>
                                                            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                                                            <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                                                            <line x1="23" y1="9" x2="17" y2="15"/>
                                                            <line x1="17" y1="9" x2="23" y2="15"/>
                                                        </>
                                                    )}
                                                </svg>
                                            </span>
                                            <span>{voiceEnabled ? 'Voice On' : 'Voice Off'}</span>
                                        </button>

                                        <button
                                            className="plus-menu-item compact"
                                            onClick={() => {
                                                setInput("Generate an image of ");
                                                setShowPlusMenu(false);
                                                setTimeout(() => {
                                                    const textarea = document.querySelector('.chat-input');
                                                    if (textarea) (textarea as HTMLTextAreaElement).focus();
                                                }, 100);
                                            }}
                                        >
                                            <span className="plus-menu-icon">
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                                    <circle cx="8.5" cy="8.5" r="1.5"/>
                                                    <polyline points="21 15 16 10 5 21"/>
                                                </svg>
                                            </span>
                                            <span>Generate Image</span>
                                        </button>

                                        <button
                                            className={`plus-menu-item compact ${selectedMode === 'research' ? 'active' : ''}`}
                                            onClick={() => {
                                                setSelectedMode(selectedMode === 'research' ? 'quick' : 'research');
                                                setShowPlusMenu(false);
                                            }}
                                        >
                                            <span className="plus-menu-icon">
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <circle cx="11" cy="11" r="8"/>
                                                    <path d="M21 21l-4.35-4.35"/>
                                                    <path d="M11 8v6M8 11h6"/>
                                                </svg>
                                            </span>
                                            <span>Deep Research</span>
                                        </button>


                                        <button
                                            className={`plus-menu-item compact ${selectedMode === 'formfill' ? 'active' : ''}`}
                                            onClick={() => {
                                                setSelectedMode(selectedMode === 'formfill' ? 'quick' : 'formfill');
                                                setShowPlusMenu(false);
                                            }}
                                        >
                                            <span className="plus-menu-icon">
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                                    <polyline points="14 2 14 8 20 8"/>
                                                    <line x1="16" y1="13" x2="8" y2="13"/>
                                                    <line x1="16" y1="17" x2="8" y2="17"/>
                                                </svg>
                                            </span>
                                            <span>Form Filler</span>
                                        </button>
                                    </div>

                                    {/* Admin Section */}
                                    {userToken && isAdmin && (
                                        <>
                                            <div className="plus-menu-divider"></div>
                                            <button
                                                className="plus-menu-item compact"
                                                onClick={() => {
                                                    setShowAdminDocs(true);
                                                    setShowPlusMenu(false);
                                                }}
                                            >
                                                <span className="plus-menu-icon">
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <circle cx="12" cy="12" r="3"/>
                                                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                                                    </svg>
                                                </span>
                                                <span>Admin</span>
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Hidden: Original Model Selector (keeping for reference) */}
                        <div className="model-selector hidden-redesign" ref={modelSelectorRef}>
                            <button
                                className="model-selector-btn"
                                onClick={() => setModelSelectorOpen(!modelSelectorOpen)}
                                disabled={loading}
                            >
                                <span className="model-name">
                                    {selectedMode === 'quick' ? 'Quick' : selectedMode === 'research' ? 'Deep Research' : 'Form Filler'}
                                </span>
                                <svg className={`chevron ${modelSelectorOpen ? 'open' : ''}`} width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>

                            {modelSelectorOpen && (
                                <div className="model-dropdown">
                                    <div className="model-dropdown-header">Takku</div>

                                    <div
                                        className={`model-option ${selectedMode === 'quick' ? 'selected' : ''}`}
                                        onClick={() => {
                                            setSelectedMode('quick');
                                            setModelSelectorOpen(false);
                                        }}
                                    >
                                        <div className="model-option-content">
                                            <div className="model-option-name">Quick</div>
                                            <div className="model-option-desc">Answers quickly with web search</div>
                                        </div>
                                        {selectedMode === 'quick' && (
                                            <svg className="checkmark" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        )}
                                    </div>

                                    <div
                                        className={`model-option ${selectedMode === 'research' ? 'selected' : ''}`}
                                        onClick={() => {
                                            setSelectedMode('research');
                                            setModelSelectorOpen(false);
                                        }}
                                    >
                                        <div className="model-option-content">
                                            <div className="model-option-name">
                                                Deep Research
                                                <span className="model-badge">Pro</span>
                                            </div>
                                            <div className="model-option-desc">Comprehensive multi-source analysis</div>
                                        </div>
                                        {selectedMode === 'research' && (
                                            <svg className="checkmark" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        )}
                                    </div>


                                    <div
                                        className={`model-option ${selectedMode === 'formfill' ? 'selected' : ''}`}
                                        onClick={() => {
                                            setSelectedMode('formfill');
                                            setModelSelectorOpen(false);
                                        }}
                                    >
                                        <div className="model-option-content">
                                            <div className="model-option-name">
                                                Form Filler
                                                <span className="model-badge formfill">New</span>
                                            </div>
                                            <div className="model-option-desc">Fill PDF & Word forms with your data</div>
                                        </div>
                                        {selectedMode === 'formfill' && (
                                            <svg className="checkmark" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                <path d="M13.5 4.5L6 12L2.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <textarea
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyPress={handleKeyPress}
                            onPaste={handlePaste}
                            placeholder="Message Takku..."
                            rows={1}
                        />
                        <button
                            type="button"
                            className="voice-input-button"
                            onClick={() => {
                                if (!userToken) {
                                    // Guests can use the text chat, but voice mode is a real
                                    // per-second-billed Gemini Live session -- gated server-side
                                    // too (see /voice/ws/browser-stream). Send them to log in
                                    // instead of attempting a connection that's certain to 403.
                                    setShowLogin(true);
                                    return;
                                }
                                unlockAudio(); // user gesture -- needed for the overlay's <audio> autoplay
                                setTalkToTakkuOpen(true);
                            }}
                            title="Talk to Takku"
                            aria-label="Talk to Takku"
                        >
                            {/* Same diamond icon VoiceInput used -- now launches full voice mode
                                instead of single-shot dictation (see add_talk_to_takku.py). */}
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M12 2L22 12L12 22L2 12L12 2Z" fill="none" />
                                <line x1="5" y1="8" x2="19" y2="8" />
                                <line x1="4" y1="10" x2="20" y2="10" />
                                <line x1="3" y1="12" x2="21" y2="12" />
                                <line x1="4" y1="14" x2="20" y2="14" />
                                <line x1="5" y1="16" x2="19" y2="16" />
                            </svg>
                        </button>
                        <button
                            className="send-btn"
                            onClick={sendMessage}
                            disabled={loading || !input.trim()}
                        >
                            ↑
                        </button>
                    </div>

                    <div className="action-buttons hidden-redesign">
                        <button
                            className={`action-btn ${voiceEnabled ? 'active' : ''}`}
                            onClick={() => {
                                unlockAudio(); // Unlock audio on iOS
                                setVoiceEnabled(!voiceEnabled);
                            }}
                            title="Toggle voice"
                        >
                            {voiceEnabled ? '🔊 Voice On' : '🔇 Voice Off'}
                        </button>

                        <button
                            className="action-btn"
                            onClick={() => fileInputRef.current?.click()}
                            title="Attach image"
                        >
                            📎 Attach
                        </button>

                        {/* Document Upload for RAG */}
                        <DocumentUpload
                            apiUrl={API_BASE_URL}
                            userToken={userToken}
                            onUploadComplete={() => setDocRefresh(prev => prev + 1)}
                        />

                        {userToken && (
                            <button
                                className={`action-btn ${showMyDocs ? 'active' : ''}`}
                                onClick={() => {
                                    setShowMyDocs(!showMyDocs);
                                    setShowAdminDocs(false);
                                }}
                                title="My Documents"
                            >
                                📚 My Docs
                            </button>
                        )}

                        {userToken && isAdmin && (
                            <button
                                className={`action-btn ${showAdminDocs ? 'active' : ''}`}
                                onClick={() => {
                                    setShowAdminDocs(!showAdminDocs);
                                    setShowMyDocs(false);
                                }}
                                title="Admin Panel"
                            >
                                🔐 Admin
                            </button>
                        )}

                        {/* Phase 2: Memory & Reminder buttons */}
                        {userToken && (
                            <>
                                <button
                                    className={`action-btn ${showMemoryManager ? 'active' : ''}`}
                                    onClick={() => setShowMemoryManager(!showMemoryManager)}
                                    title="Memory Manager"
                                >
                                    🧠 Memory
                                </button>
                                <button
                                    className={`action-btn ${showReminders ? 'active' : ''}`}
                                    onClick={() => setShowReminders(!showReminders)}
                                    title="Reminders"
                                >
                                    ⏰ Reminders
                                </button>
                            </>
                        )}

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*,.pdf,.docx,.txt,.py,.ipynb"
                            multiple
                            style={{ display: 'none' }}
                            onChange={handleFileSelect}
                        />

                        {/* Hidden Camera Input */}
                        <input
                            ref={cameraInputRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            style={{ display: 'none' }}
                            onChange={handleFileSelect}
                        />
                    </div>

                    <div className="footer">
                        <span className="footer-text">Trusted by Manu Raveendran & Friends</span>
                        <span className="footer-links">
                            <a href="/privacy.html" target="_blank" rel="noopener noreferrer">Privacy</a>
                            {" | "}
                            <a href="/terms.html" target="_blank" rel="noopener noreferrer">Terms</a>
                        </span>
                    </div>
                </div>

                {/* Login Modal */}
                {showLogin && (
                    <Login
                        apiUrl={API_BASE_URL}
                        onLoginSuccess={handleLoginSuccess}
                        onClose={() => setShowLogin(false)}
                    />
                )}

                {/* My Documents Panel */}
                <MyDocuments
                    apiUrl={API_BASE_URL}
                    userToken={userToken}
                    isOpen={showMyDocs}
                    onClose={() => setShowMyDocs(false)}
                    onRefresh={docRefresh}
                />

                {/* Admin Documents Panel */}
                {isAdmin && (
                    <AdminDocuments
                        apiUrl={API_BASE_URL}
                        userToken={userToken}
                        isOpen={showAdminDocs}
                        onClose={() => setShowAdminDocs(false)}
                    />
                )}

                {/* Phase 2: Memory Manager Panel */}
                <MemoryManager
                    apiUrl={API_BASE_URL}
                    userToken={userToken}
                    isOpen={showMemoryManager}
                    onClose={() => setShowMemoryManager(false)}
                />

                {/* Phase 2: Reminder Panel */}
                <ReminderPanel
                    apiUrl={API_BASE_URL}
                    userToken={userToken}
                    isOpen={showReminders}
                    onClose={() => setShowReminders(false)}
                />

                {/* Phase 2: Settings Modal */}
                {showSettings && (
                    <SettingsModal
                        apiUrl={API_BASE_URL}
                        userToken={userToken}
                        isOpen={showSettings}
                        onClose={() => setShowSettings(false)}
                        theme={theme}
                        setTheme={setTheme}
                        currentUser={currentUser}
                        onLogin={() => setShowLogin(true)}
                        onLogout={handleLogout}
                        onCreateAccount={() => setShowLogin(true)}
                    />
                )}
            </div>
        </>
    );
};

export default App;
